/** @file currentDoc: the confirmed document with this client's pending commands replayed on top. */
import { applyCommand } from '@pose2d/commands/applyCommand';
import { lookupCommand } from '@pose2d/commands/lookupCommand';
import { ids } from '@pose2d/format/ids';
import type { Doc, Id, IdPrefix } from '@pose2d/format/types';
import type { DocState } from './docStore';
import { sessionStore } from './sessionStore';
import type { PendingCommand } from './types';
import { viewCache } from './viewCache';

/** One pending command with the ids it minted first (fresh ones past them); a refusal leaves `doc` unchanged. */
function replayOne(doc: Doc, cmd: PendingCommand, actor: string): Doc {
  let next = 0;
  const newId = (prefix: IdPrefix): Id => cmd.ids[next++] ?? ids.create(prefix, Math.random, Date.now());
  try {
    return applyCommand(doc, cmd, { lookup: lookupCommand, now: doc.meta.updatedAt, actor, newId }).doc;
  } catch {
    return doc;
  }
}

/** What panels show (`useStore(docStore, currentDoc)`): `confirmed` when nothing is pending, else the replay, cached
 *  per slice object so the selector is stable (E8-R5). */
export function currentDoc(state: DocState): Doc | null {
  const { slice } = state;
  if (slice === null) return null;
  if (slice.pending.length === 0) return slice.confirmed;
  const cached = viewCache.get(slice);
  if (cached !== undefined) return cached;
  const actor = sessionStore.getState().actor?.name ?? 'tk';
  const view = slice.pending.reduce((doc, cmd) => replayOne(doc, cmd, actor), slice.confirmed);
  viewCache.set(slice, view);
  return view;
}
